import React, { createContext, useCallback, useState, useContext, useRef } from 'react';
import WarehouseService from 'services/WarehouseService';
import { AlertMessagesContext } from 'react-alert-messages';

export const WarehouseContext = createContext();

function WarehouseProvider({ children }) {
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseProducts, setWarehouseProducts] = useState([]);
  const warehousesCacheRef = useRef(null);
  const warehouseProductsCacheRef = useRef(null);

  const fetchData = useCallback(
    async (fetchFunction, { params = {} } = {}) => {
      try {
        if (params.paginated) {
          const limit = params.limit || 500;
          let offset = params.offset || 0;
          let allData = [];

          while (true) {
            const resp = await fetchFunction({
              offset,
              limit,
              ...params.extra,
            });
            const data = resp?.results || resp?.data || resp || [];
            const count = resp?.count || data.length;

            allData = [...allData, ...data];
            if (allData.length >= count) break;
            offset += limit;
          }

          return allData;
        }

        const resp = await fetchFunction(params.extra || {});
        return resp;
      } catch (error) {
        postAlertMessage?.({
          text: error.message || 'Failed to fetch data',
          type: 'failed',
          timeout: 5000,
        });
        return [];
      }
    },
    [postAlertMessage]
  );

  const getWarehouses = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && warehousesCacheRef.current) {
        return warehousesCacheRef.current;
      }
      try {
        const resp = await WarehouseService.getWarehouses();
        warehousesCacheRef.current = resp;
        setWarehouses(resp);
        return resp;
      } catch (error) {
        postAlertMessage?.({
          text: error.message || 'Failed to fetch warehouses',
          type: 'failed',
          timeout: 5000,
        });
        return [];
      }
    },
    [postAlertMessage]
  );

  const fetchWarehouseProducts = useCallback(
    () =>
      fetchData(WarehouseService.getWarehouseProducts, {
        params: {
          paginated: false,
          extra: {},
        },
      }),
    [fetchData]
  );

  const getWarehouseProducts = useCallback(
    async ({ useCache = true } = {}) => {
      if (useCache && warehouseProductsCacheRef.current) {
        return warehouseProductsCacheRef.current;
      }
      const _products = await fetchWarehouseProducts();
      warehouseProductsCacheRef.current = _products;
      setWarehouseProducts(_products);
      return _products;
    },
    [fetchWarehouseProducts]
  );

  const importDefaultStock = useCallback(
    async (warehouseId) => {
      try {
        const resp = await WarehouseService.importDefaultStock(warehouseId);
        await getWarehouseProducts({ useCache: false });
        return resp;
      } catch (error) {
        postAlertMessage?.({
          text: error.message || 'Failed to import default stock',
          type: 'failed',
          timeout: 5000,
        });
        throw error;
      }
    },
    [getWarehouseProducts, postAlertMessage]
  );

  const handleSetWarehouseProducts = useCallback((data) => {
    if (typeof data === 'function') {
      setWarehouseProducts((prev) => {
        const next = data(prev);
        warehouseProductsCacheRef.current = next;
        return next;
      });
    } else {
      warehouseProductsCacheRef.current = data;
      setWarehouseProducts(data);
    }
  }, []);

  const localStockUpdate = useCallback(
    ({ warehouseId, itemDeltas = {}, items = [] } = {}) => {
      const mergedItemDeltas = { ...itemDeltas };
      if (Array.isArray(items)) {
        items.forEach((item) => {
          const id = item.item_id || item.id || item.item?.id;
          const qty = Number(item.quantity ?? item.delta ?? 0);
          if (id != null) {
            mergedItemDeltas[String(id)] = (mergedItemDeltas[String(id)] || 0) + qty;
          }
        });
      }

      const targetWhId = warehouseId === 0 ? null : warehouseId;
      if (!targetWhId || Object.keys(mergedItemDeltas).length === 0) return;

      setWarehouseProducts((prevWp) => {
        const currentList = prevWp || warehouseProductsCacheRef.current;
        if (!Array.isArray(currentList)) return prevWp;

        let changed = false;
        const updated = currentList.map((wp) => {
          const wpWhId = wp.warehouse?.id || wp.warehouse || null;
          const wpItemId = wp.item?.id || wp.item || null;
          if (String(wpWhId) === String(targetWhId) && wpItemId != null) {
            const key = String(wpItemId);
            if (Object.prototype.hasOwnProperty.call(mergedItemDeltas, key)) {
              const delta = Number(mergedItemDeltas[key]) || 0;
              if (delta !== 0) {
                changed = true;
                const currentStock = Number(wp.stock) || 0;
                return {
                  ...wp,
                  stock: currentStock - delta,
                };
              }
            }
          }
          return wp;
        });

        if (changed) {
          warehouseProductsCacheRef.current = updated;
          return updated;
        }
        return prevWp;
      });
    },
    []
  );

  return (
    <WarehouseContext.Provider
      value={{
        getWarehouses,
        warehouses,
        setWarehouses,
        getWarehouseProducts,
        warehouseProducts,
        setWarehouseProducts: handleSetWarehouseProducts,
        localStockUpdate,
        importDefaultStock,
      }}
    >
      {children}
    </WarehouseContext.Provider>
  );
}

export default WarehouseProvider;
